import { NextPageContext } from 'next/types';
import { ReactElement, useMemo } from 'react';
import { IsLoginChecker } from '../../common/checkers/IsLoginChecker';
import { TrackPageViewContainer } from '../../common/containers/TrackPageViewContainer';
import { t } from '../../common/getTranslations';
import { redirectInInitialProps } from '../../common/redirectInInitialProps';
import { requestContext } from '../../common/requestContext';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import { TabPositionControl } from '../../design-system/atoms/TabPositionControl/TabPositionControl';
import { Layout } from '../../design-system/layout/Layout';
import { MainTab } from '../../design-system/organisms/MainTab/MainTab';
import { MY_NEWS_TAB_LIST } from '../../domain/mynews/constant';
import { MyNewsContainer } from '../../domain/mynews/container/MyNewsContainer';
import { setNeedRefresh } from '../../domain/mynews/util/myNewsSlice';
import { selectTabStateKeyByTabType } from '../../domain/mynews/util/selector';
import { ApiErrorBoundary } from '../../errors/ApiErrorBoundary.class';
import { MyNewsTabType } from '../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../header/constants';
import { ReactQueryFetcher } from '../../reactQuery/ReactQueryFetcher';
import { useMyNewsQuery } from '../../wrapper';
function MyNews({
  tab
}: {
  tab: MyNewsTabType;
}) {
  const {
    isMobile,
    isWebView
  } = requestContext.requestInfo.display;
  const dispatch = useTypedDispatch();
  const currentTabName = MY_NEWS_TAB_LIST.find(tabItem => tabItem.id === tab)?.text;
  const needRefresh = useTypedSelector(state => state.myNews[selectTabStateKeyByTabType(tab)].needRefresh);

  // needRefresh 값이 변경되어도 다시 refetch하지 않도록 메모이제이션 합니다.
  const myNewsListInput = useMemo(() => ({
    tab,
    refresh: needRefresh
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [tab]);
  return <>
      <TabPositionControl depth={1} isMobile={isMobile} isWebView={isWebView} disabled={!isMobile}>
        <MainTab className={!isMobile ? 'px-15pxr' : undefined} isMobile={isMobile} title={t('common_tab_notification')} tabItems={MY_NEWS_TAB_LIST} currentTabItemId={tab} showBorder={true} />
      </TabPositionControl>
      <IsLoginChecker>
        <ApiErrorBoundary>
          <ReactQueryFetcher reactQuery={useMyNewsQuery} variables={{
          myNewsListInput
        }} onSuccess={() => {
          dispatch(setNeedRefresh({
            tabType: tab,
            needRefresh: false
          }));
        }}>
            {data => <MyNewsContainer tabType={tab} data={data.myNewsList} myNewsListInput={myNewsListInput} />}
          </ReactQueryFetcher>
        </ApiErrorBoundary>
      </IsLoginChecker>
      <TrackPageViewContainer trackObj={{
      section: '더보기',
      page: `알림_${currentTabName}`,
      actionType: 'Pageview',
      actionName: `알림_${currentTabName}_화면`,
      customProps: {
        user_action: 'pv'
      }
    }} />
    </>;
}
MyNews.getInitialProps = async (context: NextPageContext) => {
  const tabParams = context.query?.pathParams as string[];
  const tab = tabParams && tabParams.length > 0 ? tabParams[0].toUpperCase() : MyNewsTabType.All;
  if (Object.values(MyNewsTabType).find(type => tab === type)) {
    return {
      tab
    };
  } else {
    redirectInInitialProps({
      destination: '/404',
      res: context.res
    });
    return {};
  }
};
MyNews.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={t('common_tab_notification')} mobileLayoutConfig={{
    shouldShowHeader: true,
    headerType: CommonMobileHeaderType.ThreeDepth,
    headerCustomTiaraObject: {
      click: {
        layer1: '네비게이션'
      }
    }
  }} pcLayoutConfig={{
    shouldShowHeader: true
  }}>
      {page}
    </Layout>;
};
export default MyNews;